import { ExternalLink, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FlatfinderEstate } from 'realitiq-utils/src/flatfinder';
import { Modal } from '@/components/modals/new/modal';
import { Button } from '@/components/ui/button';
import {
  formatFlatfinderNumber,
  formatFlatfinderPrice,
  getFlatfinderAvailabilityColors,
  getFlatfinderAvailabilityLabel,
  getFlatfinderImageUrl,
  getFlatfinderLanguage,
  getSafeFlatfinderContactUrl,
  getSafeFlatfinderExternalUrl,
} from './display';
import { DigitalTwinLinkButton } from './viewer-estate-list-item';

function EstateFact({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '') return null;

  return (
    <div>
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-semibold text-slate-900">{value}</dd>
    </div>
  );
}

function EstateFacts({
  buildingName,
  estate,
}: {
  buildingName?: string;
  estate: FlatfinderEstate;
}) {
  const language = getFlatfinderLanguage();
  const { t } = useTranslation();
  const formatArea = (value: number | null) =>
    value === null || value <= 0 ? null : `${formatFlatfinderNumber(value, language)} m²`;
  const formatCount = (value: number | null) =>
    value === null || value <= 0 ? null : formatFlatfinderNumber(value, language);
  const marketType = estate.marketType === 'buy' ? t('purchase') : t('rent');

  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
      <EstateFact label={t('apartmentNumber')} value={estate.topNumber} />
      <EstateFact label={t('building')} value={buildingName} />
      <EstateFact label={t('marketType')} value={marketType} />
      <EstateFact
        label={estate.marketType === 'buy' ? t('purchasePriceSection') : t('rentalPrice')}
        value={
          estate.price === null
            ? null
            : formatFlatfinderPrice(estate.price, estate.currency, language)
        }
      />
      <EstateFact label={t('livingArea')} value={formatArea(estate.livingArea)} />
      <EstateFact label={t('usableArea')} value={formatArea(estate.usableArea)} />
      <EstateFact
        label={t('rooms')}
        value={estate.rooms === null ? null : formatFlatfinderNumber(estate.rooms, language)}
      />
      <EstateFact
        label={t('floor')}
        value={estate.floor === null ? null : formatFlatfinderNumber(estate.floor, language)}
      />
      <EstateFact label={t('numberOfBedrooms')} value={formatCount(estate.bedrooms)} />
      <EstateFact label={t('numberOfBathrooms')} value={formatCount(estate.bathrooms)} />
      <EstateFact label={t('numberOfParkingSpaces')} value={formatCount(estate.parkingSpaces)} />
      <EstateFact label={t('buildingYear')} value={estate.constructionYear} />
      <EstateFact label={t('balconyArea')} value={formatArea(estate.balconyArea)} />
      <EstateFact label={t('terraceArea')} value={formatArea(estate.terraceArea)} />
      <EstateFact label={t('gardenArea')} value={formatArea(estate.gardenArea)} />
      <EstateFact
        label={t('accessible')}
        value={estate.accessibility === null ? null : estate.accessibility ? t('yes') : t('no')}
      />
    </dl>
  );
}

export function EstateDetailModal({
  buildingName,
  estate,
  contactLink,
  publicId,
  authenticatedProjectId,
  onClose,
}: {
  buildingName?: string;
  estate: FlatfinderEstate;
  contactLink: string | null;
  publicId: string | null;
  authenticatedProjectId: string | null;
  onClose: () => void;
}) {
  const language = getFlatfinderLanguage();
  const { t } = useTranslation();
  const listingUrl = getSafeFlatfinderExternalUrl(estate.externalLink);
  const safeContactLink = getSafeFlatfinderContactUrl(contactLink);
  const availability = getFlatfinderAvailabilityColors(estate.availability);

  return (
    <Modal
      isOpen
      open={() => {}}
      dismiss={onClose}
      title={estate.name}
      className="max-h-[90dvh] w-[calc(100%-2rem)] max-w-xl overflow-y-auto rounded-lg border border-primary-light/20 shadow-lg"
    >
      <Modal.Body autoFocus tabIndex={-1} className="outline-none">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            {estate.availability ? (
              <span
                className={`mb-2 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${availability.badge}`}
              >
                {getFlatfinderAvailabilityLabel(estate.availability, language)}
              </span>
            ) : null}
          </div>
          {estate.digitalTwinLinkToken ? (
            <DigitalTwinLinkButton token={estate.digitalTwinLinkToken} />
          ) : null}
        </div>

        <EstateFacts buildingName={buildingName} estate={estate} />
        {estate.description ? (
          <p className="mt-5 whitespace-pre-line text-sm leading-6 text-slate-600">
            {estate.description}
          </p>
        ) : null}

        {estate.floorPlans.length > 0 ? (
          <div className="mt-6">
            <h3 className="mb-3 text-sm font-semibold text-slate-900">{t('floorPlans')}</h3>
            <div className="grid grid-cols-2 gap-3">
              {estate.floorPlans.map((floorPlan) => (
                <a
                  className="group overflow-hidden rounded-xl border border-slate-200 bg-slate-50"
                  href={getFlatfinderImageUrl(floorPlan.mediaId, publicId, authenticatedProjectId)}
                  key={floorPlan.id}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {floorPlan.fileType?.startsWith('image/') ||
                  (!floorPlan.fileType && /\.(avif|jpe?g|png|webp)$/i.test(floorPlan.name)) ? (
                    <img
                      alt={floorPlan.name}
                      className="aspect-[4/3] w-full object-contain transition-transform group-hover:scale-[1.02]"
                      loading="lazy"
                      crossOrigin={authenticatedProjectId ? 'use-credentials' : undefined}
                      src={getFlatfinderImageUrl(
                        floorPlan.mediaId,
                        publicId,
                        authenticatedProjectId
                      )}
                    />
                  ) : (
                    <span className="flex aspect-[4/3] w-full items-center justify-center bg-slate-100 text-slate-500">
                      <FileText className="size-10" />
                    </span>
                  )}
                  <span className="block truncate px-3 py-2 text-xs font-medium text-slate-700">
                    {floorPlan.name}
                  </span>
                </a>
              ))}
            </div>
          </div>
        ) : null}

        {listingUrl || safeContactLink ? (
          <div className="mt-6 grid gap-2 sm:grid-cols-2">
            {listingUrl ? (
              <Button asChild variant="outline" className="gap-2">
                <a href={listingUrl} rel="noopener noreferrer" target="_blank">
                  {t('openListing')}
                  <ExternalLink className="size-4" />
                </a>
              </Button>
            ) : null}
            {safeContactLink ? (
              <Button asChild variant="contained" className="gap-2">
                <a href={safeContactLink} rel="noopener noreferrer" target="_blank">
                  {t('contact')}
                  <ExternalLink className="size-4" />
                </a>
              </Button>
            ) : null}
          </div>
        ) : null}
      </Modal.Body>
    </Modal>
  );
}
